import {
  RuleStyle,
  ThreeDPositionDataMapping,
  ThreeDPositionStyle,
} from "@/shared/domain/layouts/LayoutRecord";
import { schemaValidator } from "@/shared/SchemaValidator";
import { LoggerService } from "@/shared/services";
import {
  type File,
  type State,
  type PanelState,
  type TopicData,
  CURRENT_VIZ_SCHEMA_VERSION,
  PanelType,
  LogPanelState,
  ImagePanelState,
  MapPanelState,
  PlotPanelState,
  RawMessagePanelState,
} from "@/shared/state/visualization";
import {
  Datasource,
  PanelStates,
  ThreeDPanelState,
} from "@/shared/state/visualization/schema";
import * as threeDSchema from "@/shared/state/visualization/schema/v1/three-d";

import { InvalidVizConfig } from "./exception";

interface PanelData {
  data: TopicData[];
}

/**
 * Representation of visualization state responsible for serialization, deserialization,
 * and providing a convenient interface for reading visualization data.
 */
export class VizConfig {
  readonly #state: State;

  /**
   * Create an empty visualization state.
   */
  public static default(): State {
    return {
      version: CURRENT_VIZ_SCHEMA_VERSION,
      files: [],
      datasources: [],
      layout: {
        id: "root",
        axis: "x",
        relativeSize: 1,
        children: [],
      },
      panels: {},
    };
  }

  public static from_obj(obj: unknown): VizConfig {
    if (VizConfig.isValid(obj)) {
      return new VizConfig(obj);
    }

    LoggerService.error("Unable to instantiate VizConfig:", obj);
    throw new InvalidVizConfig("Unable to instantiate VizConfig");
  }

  public static isValid(state: unknown): state is State {
    return schemaValidator.validate<State>(CURRENT_VIZ_SCHEMA_VERSION, state);
  }

  /**
   * Returns the topic data for a specific panel, organized based on the panel type.
   */
  static getDataForPanel(panel: PanelState): PanelData[] {
    return exhaustiveSwitch<PanelType, PanelData[]>(panel.type, {
      [PanelType.Log]: () => {
        const logPanel = panel as LogPanelState;
        return logPanel.data.map((data) => ({ data: [data] }));
      },
      [PanelType.Image]: () => {
        const imagePanel = panel as ImagePanelState;
        return imagePanel.data.map((clip) => ({ data: [clip.data] }));
      },
      [PanelType.Plot]: () => {
        const plotPanel = panel as PlotPanelState;
        return plotPanel.data.map((series) => ({ data: [series.data] }));
      },
      [PanelType.Map]: () => {
        const mapPanel = panel as MapPanelState;
        return mapPanel.data.map((mapPath) => ({ data: mapPath.data }));
      },
      [PanelType.ThreeD]: () => {
        const threeDPanel = panel as ThreeDPanelState;
        return Object.values(threeDPanel.data.entities).map((e) => ({
          data: extractTopicDataFromPosition(e.position).topicData,
        }));
      },
      [PanelType.RawMessage]: () => {
        const rawMessagePanel = panel as RawMessagePanelState;
        return rawMessagePanel.data ? [{ data: [rawMessagePanel.data] }] : [];
      },
    });
  }

  /**
   * Returns the data and style information for a specific panel's data items.
   */
  static getDataAndStylesForPanel(panel: PanelState): {
    data: TopicData[];
    style: RuleStyle | undefined;
  }[] {
    return exhaustiveSwitch<
      PanelType,
      { data: TopicData[]; style: RuleStyle | undefined }[]
    >(panel.type, {
      [PanelType.Log]: () => {
        const logPanel = panel as LogPanelState;
        return logPanel.data.map((data) => ({
          data: [data],
          style: undefined,
        }));
      },
      [PanelType.Image]: () => {
        const imagePanel = panel as ImagePanelState;
        return imagePanel.data.map((clip) => ({
          data: [clip.data],
          style: undefined,
        }));
      },
      [PanelType.Plot]: () => {
        const plotPanel = panel as PlotPanelState;
        return plotPanel.data.map((series) => ({
          data: [series.data],
          style: {
            color: series.config?.lineColor,
            // Only set the field on the object at all if it is defined.
            ...(series.config?.lineWidth !== undefined
              ? { lineWidth: series.config.lineWidth }
              : {}),
            // Only set the field on the object at all if it is defined.
            ...(series.config?.pointRadius !== undefined
              ? { pointRadius: series.config.pointRadius }
              : {}),
            // Only set the field on the object at all if it is defined.
            ...(series.config?.yAxis !== undefined
              ? { yAxis: series.config.yAxis }
              : {}),
            // Only set the field on the object at all if it is defined.
            ...(series.config?.label !== undefined
              ? { label: series.config.label }
              : {}),
          },
        }));
      },
      [PanelType.Map]: () => {
        const mapPanel = panel as MapPanelState;
        return mapPanel.data.map((mapPath) => ({
          data: mapPath.data,
          style: {
            color: mapPath.style?.lineColor,
          },
        }));
      },
      [PanelType.ThreeD]: () => {
        const threeDPanel = panel as ThreeDPanelState;
        return Object.values(threeDPanel.data.entities).map((e) => {
          const {
            topicData: positionTopicData,
            dataMapping: positionDataMapping,
          } = extractTopicDataFromPosition(e.position);
          return {
            data: positionTopicData,
            style: {
              threeDComponents: {
                entityId: e.id,
                position: positionDataMapping,
                mesh: e.mesh,
                path: e.path,
              },
            } satisfies RuleStyle,
          };
        });
      },
      [PanelType.RawMessage]: () => {
        const rawMessagePanel = panel as RawMessagePanelState;
        return rawMessagePanel.data
          ? [{ data: [rawMessagePanel.data], style: undefined }]
          : [];
      },
    });
  }

  /**
   * Returns all topic data for a panel as a flat array.
   */
  static getTopicDataForPanel(panel: PanelState): TopicData[] {
    return this.getDataForPanel(panel).flatMap(({ data }) => data);
  }

  public constructor(state?: State) {
    this.#state = state ? this.withDatasources(state) : VizConfig.default();
  }

  public get files(): File[] {
    return this.#state.files;
  }

  public get datasources(): Datasource[] {
    return this.#state.datasources ?? [];
  }

  public get layout() {
    return this.#state.layout;
  }

  public get panels(): {
    [id: string]: PanelStates;
  } {
    return this.#state.panels ?? {};
  }

  public getPanelById(id: string): PanelState | undefined {
    return this.#state.panels?.[id];
  }

  public hasPanels(): boolean {
    return Object.keys(this.#state.panels ?? {}).length > 0;
  }

  public iterPanels(): IterableIterator<PanelState> {
    return Object.values(this.#state.panels ?? {})[Symbol.iterator]();
  }

  public toObject(): State {
    return this.#state;
  }

  public toString(): string {
    return JSON.stringify(this.#state, null, 2);
  }

  private withDatasources(state: State): State {
    return {
      ...state,
      // Backward compatibility: if datasources is not provided, use files
      datasources:
        state.datasources ??
        state.files.map((f) => ({
          type: "file",
          value: f,
        })),
    };
  }
}

type EnumCases<T extends string | number | symbol, R> = {
  [K in T]: () => R;
};
function exhaustiveSwitch<T extends string | number | symbol, R>(
  enumValue: T,
  cases: EnumCases<T, R>,
): R {
  return cases[enumValue]();
}

function extractTopicDataFromPosition(p: threeDSchema.Position | undefined): {
  topicData: TopicData[];
  dataMapping?: ThreeDPositionStyle;
} {
  if (!p) {
    return { topicData: [] };
  }
  const topicData = [p.forward, p.right, p.up]
    .filter((maybeTopicData) => maybeTopicData)
    .filter((maybeTopicData) => typeof maybeTopicData !== "number");
  const dataMappingFor = (
    value: TopicData | number,
    scale: number,
  ): ThreeDPositionDataMapping => {
    const index = topicData.findIndex((data) => data === value);
    if (index >= 0) {
      return {
        type: "topicData",
        topicDataIndex: index,
        scale,
      };
    }
    // If the topicdata list did not contain value, then it is a number.
    return {
      type: "fixed",
      value: value as number,
      scale,
    };
  };
  return {
    topicData,
    dataMapping: {
      transformation: p.transformation,
      basePreset: p.basePreset,
      right: dataMappingFor(p.right, p.rightScale),
      forward: dataMappingFor(p.forward, p.forwardScale),
      up: dataMappingFor(p.up, p.upScale),
    },
  };
}
